import { useMemo } from "react"

import * as DropdownMenu from "@radix-ui/react-dropdown-menu"

import styles from "./SettlementTableRow.module.scss"

import type { SettlementTableFieldsFragment } from "@/company_admin/gql/__generated__/graphql"
import { formatIsoDate, formatYearMonth } from "@/utils/date"
import { formatAmount, formatNullableAmount } from "@/utils/money"

import PaymentTypeBadge from "../badges/PaymentTypeBadge"
import StatusBadge from "../badges/StatusBadge"
import { getAdjustmentLabel } from "../settlement.logic"
import { UnconfirmedWarningTooltip } from "../tooltips/SettlementTooltips"

type Props = {
	settlement: SettlementTableFieldsFragment
	isSouhlAdmin: boolean
	onRecreate: (settlementId: string) => void
	onOpenRereservationStatusFix: (settlementId: string) => void
	onUpdateStatus: (settlementId: string, targetStatus: string) => void
	onSendEmail: (settlementId: string) => void
	onExcelDownload: (settlementId: string) => Promise<void>
	isExcelDownloading: boolean
}

const SettlementTableRow = ({
	settlement,
	isSouhlAdmin,
	onRecreate,
	onOpenRereservationStatusFix,
	onUpdateStatus,
	onSendEmail,
	onExcelDownload,
	isExcelDownloading,
}: Props) => {
	const s = settlement
	const adjustmentLabel = useMemo(
		() => getAdjustmentLabel(s.totalAdjustment),
		[s.totalAdjustment]
	)

	const isPending = s.status === "PENDING"
	const isInProgress =
		s.status === "PAYOUT_IN_PROGRESS" || s.status === "INVOICE_IN_PROGRESS"
	const isZeroNet = s.finalAmount.amount === 0
	const rowClassName = `${styles.row} ${isPending ? styles.pending_row : ""}`

	// "確認中にする" のターゲットステータスは isPayout で決まる
	const inProgressTargetStatus = s.finalAmount.isPayout
		? "PAYOUT_IN_PROGRESS"
		: "INVOICE_IN_PROGRESS"

	return (
		<tr className={rowClassName}>
			{/* 成果月 */}
			<td>{formatYearMonth(s.yearMonth)}</td>

			{/* 御社お支払期限 */}
			<td className={styles.payment_due_cell}>
				{s.companyPaymentDue ? formatIsoDate(s.companyPaymentDue) : "-"}
			</td>

			{/* 回収金額合計 */}
			<td className={styles.amount_cell}>
				{formatAmount(s.totalPaidCancelFee)}
			</td>

			{/* 請求管理機能 サービス手数料 */}
			<td className={styles.amount_cell}>
				{formatAmount(s.cancelFeeCommissionExclTax)}
			</td>

			{/* 再予約金額合計 */}
			<td className={styles.amount_cell}>
				{formatAmount(s.totalRereservationAmount)}
			</td>

			{/* 離脱顧客獲得機能 サービス手数料 */}
			<td className={styles.amount_cell}>
				{formatAmount(s.rereservationCommissionExclTax)}
			</td>

			{/* 前月SOUHL未振込金額 */}
			<td className={styles.amount_cell}>
				{formatNullableAmount(s.souhlCarryoverAmount)}
			</td>

			{/* 前月御社未払い金額 */}
			<td className={styles.amount_cell}>
				{formatNullableAmount(s.companyCarryoverAmount)}
			</td>

			{/* 調整金額 */}
			<td className={styles.amount_cell}>
				{formatAmount(s.totalAdjustment.amount)}
				{adjustmentLabel && (
					<span className={styles.adjustment_label}>{adjustmentLabel}</span>
				)}
			</td>

			{/* 振込手数料 */}
			<td className={styles.amount_cell}>{formatAmount(s.transferFee)}</td>

			{/* 最終金額 */}
			<td className={`${styles.amount_cell} ${styles.net_amount}`}>
				{formatAmount(s.finalAmount.amount)}
			</td>

			{/* 支払い種別 */}
			<td>
				{isZeroNet ? (
					"-"
				) : (
					<PaymentTypeBadge isPayout={s.finalAmount.isPayout} />
				)}
			</td>

			{/* ステータス */}
			<td>{isZeroNet ? "-" : <StatusBadge status={s.status} />}</td>

			{/* ダウンロードボタン */}
			<td className={styles.excel_cell}>
				<button
					type="button"
					className={styles.excel_button}
					disabled={isExcelDownloading}
					onClick={() => {
						onExcelDownload(s.id)
					}}
				>
					{isExcelDownloading ? (
						<span className={styles.excel_loading_icon} />
					) : (
						<span className={styles.excel_icon}>↓</span>
					)}
					<span className={styles.excel_text}>
						{s.finalAmount.isPayout ? (
							<>
								明細 Excel
								<br />
								ダウンロード
							</>
						) : (
							<>
								請求書 PDF・明細 Excel
								<br />
								ダウンロード
							</>
						)}
					</span>
				</button>
			</td>

			{/* ⚠ 再予約未確定アイコン */}
			<td className={styles.warning_cell}>
				{s.hasUnconfirmedRereservation && <UnconfirmedWarningTooltip />}
			</td>

			{/* ⋮ メニュー（souhl_admin のみ） */}
			{isSouhlAdmin && (
				<td>
					<DropdownMenu.Root modal={false}>
						<DropdownMenu.Trigger asChild>
							<button type="button" className={styles.menu_button}>
								&#8942;
							</button>
						</DropdownMenu.Trigger>
						<DropdownMenu.Portal>
							<DropdownMenu.Content
								className={styles.menu_dropdown}
								align="end"
								sideOffset={4}
							>
								<DropdownMenu.Item
									className={styles.menu_item}
									onSelect={() => onOpenRereservationStatusFix(s.id)}
								>
									再予約ステータス修正
								</DropdownMenu.Item>
								<DropdownMenu.Separator className={styles.menu_separator} />
								<DropdownMenu.Item
									className={styles.menu_item}
									onSelect={() => onRecreate(s.id)}
								>
									アーカイブ&再作成
								</DropdownMenu.Item>
								<DropdownMenu.Separator className={styles.menu_separator} />
								{/* ステータス遷移 */}
								<DropdownMenu.Item
									className={styles.menu_item}
									disabled={!isPending}
									onSelect={() => onUpdateStatus(s.id, inProgressTargetStatus)}
								>
									確認中にする
								</DropdownMenu.Item>
								<DropdownMenu.Item
									className={styles.menu_item}
									disabled={!isInProgress}
									onSelect={() => onUpdateStatus(s.id, "PENDING")}
								>
									保留中に戻す
								</DropdownMenu.Item>
								<DropdownMenu.Item
									className={styles.menu_item}
									disabled={!isInProgress}
									onSelect={() => onUpdateStatus(s.id, "COMPLETED")}
								>
									完了にする
								</DropdownMenu.Item>
								<DropdownMenu.Item
									className={styles.menu_item}
									disabled={!isInProgress}
									onSelect={() => onUpdateStatus(s.id, "CARRYOVER")}
								>
									来月繰越にする
								</DropdownMenu.Item>
								<DropdownMenu.Separator className={styles.menu_separator} />
								<DropdownMenu.Item
									className={styles.menu_item}
									disabled={!isInProgress}
									onSelect={() => onSendEmail(s.id)}
								>
									メール送信
								</DropdownMenu.Item>
							</DropdownMenu.Content>
						</DropdownMenu.Portal>
					</DropdownMenu.Root>
				</td>
			)}
		</tr>
	)
}

export default SettlementTableRow
